import { z } from 'zod';
import { EntityType } from './enums.js';
import { IsoDate, IsoDateTime, UuidString } from './primitives.js';

/**
 * Tool contracts — I/O Zod-схемы для каждого tool из gateway registry.
 * Input/Output валидируется в gateway invoker обязательно.
 */

// ─── read_transactional_records ──────────────────────────────────────

export const ReadTransactionalRecordsInput = z
  .object({
    entity_id: UuidString,
    record_kind: z.enum([
      'stock_entry',
      'stock_ledger_entry',
      'stock_reconciliation',
      'work_order',
      'landed_cost',
      'movement_event',
    ]),
    filter: z
      .object({
        date_from: IsoDate.optional(),
        date_to: IsoDate.optional(),
        item_id: UuidString.optional(),
        warehouse_id: UuidString.optional(),
        voucher_id: UuidString.optional(),
      })
      .strict()
      .optional(),
    limit: z.number().int().min(1).max(1000).default(100),
    cursor: z.string().optional(),
  })
  .strict();
export type ReadTransactionalRecordsInput = z.infer<typeof ReadTransactionalRecordsInput>;

export const ReadTransactionalRecordsOutput = z
  .object({
    records: z.array(z.record(z.unknown())),
    next_cursor: z.string().nullable().optional(),
    total_hint: z.number().int().nonnegative().optional(),
  })
  .strict();
export type ReadTransactionalRecordsOutput = z.infer<typeof ReadTransactionalRecordsOutput>;

// ─── read_master_data ────────────────────────────────────────────────

export const ReadMasterDataInput = z
  .object({
    entity_id: UuidString,
    master_kind: z.enum([
      'item',
      'item_group',
      'warehouse',
      'batch',
      'bom',
      'uom',
      'uom_conversion',
    ]),
    filter: z.record(z.unknown()).optional(),
    limit: z.number().int().min(1).max(1000).default(100),
    cursor: z.string().optional(),
  })
  .strict();
export type ReadMasterDataInput = z.infer<typeof ReadMasterDataInput>;

export const ReadMasterDataOutput = z
  .object({
    records: z.array(z.record(z.unknown())),
    next_cursor: z.string().nullable().optional(),
  })
  .strict();
export type ReadMasterDataOutput = z.infer<typeof ReadMasterDataOutput>;

// ─── query_stock_balance ─────────────────────────────────────────────

export const QueryStockBalanceInput = z
  .object({
    entity_id: UuidString,
    as_of: IsoDateTime.optional(),
    item_id: UuidString.optional(),
    warehouse_id: UuidString.optional(),
    batch_id: UuidString.optional(),
    limit: z.number().int().min(1).max(5000).default(500),
  })
  .strict();
export type QueryStockBalanceInput = z.infer<typeof QueryStockBalanceInput>;

export const QueryStockBalanceOutput = z
  .object({
    rows: z.array(z.record(z.unknown())),
    as_of: IsoDateTime,
  })
  .strict();
export type QueryStockBalanceOutput = z.infer<typeof QueryStockBalanceOutput>;

// ─── query_audit_log ─────────────────────────────────────────────────

export const QueryAuditLogInput = z
  .object({
    entity_id: UuidString,
    resource_type: EntityType.optional(),
    resource_id: z.string().optional(),
    actor_user_id: UuidString.optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    limit: z.number().int().min(1).max(1000).default(100),
    cursor: z.string().optional(),
  })
  .strict();
export type QueryAuditLogInput = z.infer<typeof QueryAuditLogInput>;

export const QueryAuditLogOutput = z
  .object({
    events: z.array(z.record(z.unknown())),
    next_cursor: z.string().nullable().optional(),
  })
  .strict();
export type QueryAuditLogOutput = z.infer<typeof QueryAuditLogOutput>;

// ─── S9: master-data conflict + 1C two-way sync tools ────────────────

export const ResolveMasterDataConflictInput = z
  .object({
    entity_id: UuidString,
    conflict_id: UuidString,
    action: z.enum(['keep_ours', 'accept_theirs', 'merge']),
    merge_fields: z.record(z.enum(['ours', 'theirs'])).optional(),
    reason: z.string().min(1).max(500),
  })
  .strict();
export type ResolveMasterDataConflictInput = z.infer<typeof ResolveMasterDataConflictInput>;

export const ResolveMasterDataConflictOutput = z
  .object({
    conflict_id: UuidString,
    applied_action: z.enum(['keep_ours', 'accept_theirs', 'merge']),
    local_entity_id: UuidString.nullable(),
    requires_push_to_1c: z.boolean(),
  })
  .strict();
export type ResolveMasterDataConflictOutput = z.infer<typeof ResolveMasterDataConflictOutput>;

export const PullOneCMasterInput = z
  .object({
    entity_id: UuidString,
    resources: z.array(z.enum(['item', 'warehouse', 'bom', 'counterparty', 'batch'])).min(1),
    since: IsoDateTime.optional(),
  })
  .strict();
export type PullOneCMasterInput = z.infer<typeof PullOneCMasterInput>;

export const PullOneCMasterOutput = z
  .object({
    fetched: z.number().int().nonnegative(),
    staged_diffs: z.number().int().nonnegative(),
    conflicts_created: z.number().int().nonnegative(),
  })
  .strict();
export type PullOneCMasterOutput = z.infer<typeof PullOneCMasterOutput>;

export const PushOneCMasterInput = z
  .object({
    entity_id: UuidString,
    resource: z.enum(['item', 'warehouse', 'bom', 'counterparty']),
    ids: z.array(UuidString).min(1).max(500),
  })
  .strict();
export type PushOneCMasterInput = z.infer<typeof PushOneCMasterInput>;

export const PushOneCMasterOutput = z
  .object({
    pushed: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    failed: z.array(z.object({ id: UuidString, reason: z.string() })),
  })
  .strict();
export type PushOneCMasterOutput = z.infer<typeof PushOneCMasterOutput>;

// ─── Tool registry meta (policy per tool) ────────────────────────────

export const ToolPolicy = z
  .object({
    name: z.string(),
    class: z.enum(['read', 'write', 'orchestration']),
    tier: z.number().int().min(0).max(3),
    retry: z
      .object({
        max_attempts: z.number().int().min(0).max(10),
        backoff_ms: z.number().int().min(0).max(60_000),
      })
      .strict(),
    timeout_ms: z.number().int().min(100).max(300_000),
    idempotent: z.boolean(),
    approval_required: z.boolean(),
  })
  .strict();
export type ToolPolicy = z.infer<typeof ToolPolicy>;
